import React from 'react';
import { bookingShape, historyDetailShape } from 'constants/propTypes';
import { FormattedMessage, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { isNil } from 'ramda';
import {
  ActionItemBookingItems,
  ActionItemSegmentContentCell,
  ActionItemSegmentHeader,
  ActionItemSegmentHeaderCell,
  ActionItemSegmentHotelCell,
  ActionItemSegmentContentCellHighlight,
  ActionItemFullRow
} from './HistoryDetail.styles';
import FormattedCurrency from '../../../../../../components/ui/FormattedCurrency';
import AlertBox from '../../../../../../components/ui/AlertBox';
import { DB_FEE_CODE } from 'constants/tariffCodes';
import HistoryActionPassengerId from './HistoryActionPassengerId';

import { shouldDisplayReasons, buildReasons, getBookingPassengerById } from './HistoryDetails.utils';

const HOTEL_TARIFF_CODE = 'HOTEL';
const INSURANCE_TARIFF_CODE = 'EXTERNAL_INSURANCE';

const hotelDetails = [
  { id: 'extras.hotel.eurostarPackageRef', key: 'eurostarReference' },
  { id: 'extras.hotel.jacTravelReference', key: 'jacTravelRef' },
  { id: 'extras.hotel.id', key: 'hotelId' },
  { id: 'extras.hotel.hotelName', key: 'hotelName' },
  { id: 'extras.hotel.numberOfNights', key: 'numberOfNights' },
  { id: 'extras.hotel.guestCount', key: 'guestCount' },
  { id: 'extras.hotel.roomCount', key: 'roomCount' },
  { id: 'extras.hotel.hotelAddress', key: 'hotelAddress' }
];

const HistoryActionBookingItems = ({ action, booking, detail, intl }) => {
  const { segments } = detail.data.bookingItemsActions.find((b) => b.action === action);

  const getTicketPrice = (product, isRequired = false) =>
    isRequired && product.isLoyalty ? (
      `${product.loyaltyPoints} pts`
    ) : (
      <FormattedCurrency currency={product.currency} amount={product.price} />
    );

  const getHotelTitle = (p) =>
    hotelDetails.reduce(
      (result, { id, key }) => result + (!isNil(p[key]) && p[key] !== '' ? `${intl.formatMessage({ id })}: ${p[key]}\n` : ''),
      ''
    ) || undefined;

  const getReferenceNumber = (p) => {
    switch (p.tariffCode) {
      case HOTEL_TARIFF_CODE:
        return `ref: ${p?.jacTravelRef}`;
      case INSURANCE_TARIFF_CODE:
        return `ref: ${p?.insurancePolicyNumber}`;
      default:
        return p.seat;
    }
  };

  const printProduct = (prodList, isRequired = false) =>
    prodList.map((product, index) => (
      <React.Fragment key={product.uuid || `${product.tariffCode}-${product.ticket}-${index}`}>
        <ActionItemSegmentContentCell>
          <HistoryActionPassengerId passenger={getBookingPassengerById(booking, product.passengerId)} />
        </ActionItemSegmentContentCell>
        <ActionItemSegmentContentCell>{product.passenger}</ActionItemSegmentContentCell>
        {product.tariffCode === HOTEL_TARIFF_CODE ? (
          <ActionItemSegmentHotelCell title={getHotelTitle(product)}>{product.tariffName}</ActionItemSegmentHotelCell>
        ) : (
          <ActionItemSegmentContentCell>{product.tariffName}</ActionItemSegmentContentCell>
        )}
        <ActionItemSegmentContentCell>{product.tariffCode}</ActionItemSegmentContentCell>
        <ActionItemSegmentContentCell>{getTicketPrice(product, isRequired)}</ActionItemSegmentContentCell>
        <ActionItemSegmentContentCell>{product.ticket}</ActionItemSegmentContentCell>
        <ActionItemSegmentContentCell>{getReferenceNumber(product)}</ActionItemSegmentContentCell>
        {shouldDisplayReasons(action, product) &&
          product.overrideReason.map((or) => (
            <ActionItemSegmentContentCellHighlight key={or.key} dataCy="override-reason">
              {buildReasons(or)}
            </ActionItemSegmentContentCellHighlight>
          ))}
      </React.Fragment>
    ));

  return (
    <ActionItemBookingItems>
      {segments.map((s) => (
        <React.Fragment key={s.uuid}>
          <ActionItemSegmentHeader>
            <ActionItemSegmentHeaderCell>{s.departureDate}</ActionItemSegmentHeaderCell>
            {s.train && <ActionItemSegmentHeaderCell>{s.train}</ActionItemSegmentHeaderCell>}
            {s.departureTimeStation && <ActionItemSegmentHeaderCell>{s.departureTimeStation}</ActionItemSegmentHeaderCell>}
            {s.arrivalTimeStation && <ActionItemSegmentHeaderCell>{s.arrivalTimeStation}</ActionItemSegmentHeaderCell>}
          </ActionItemSegmentHeader>

          {printProduct(s.requiredProducts, true)}
          {printProduct(s.additionalProducts)}
          {printProduct(s.fees)}
          {s.fees.some((fee) => fee.tariffCode === DB_FEE_CODE) && (
            <ActionItemFullRow>
              <AlertBox variant={AlertBox.variants.WARNING} dataCy="db-fee-history-warning">
                <FormattedMessage id="dbVoucher.issuedToCustomer" />
              </AlertBox>
            </ActionItemFullRow>
          )}
        </React.Fragment>
      ))}
    </ActionItemBookingItems>
  );
};

HistoryActionBookingItems.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func
  }),
  action: PropTypes.string.isRequired,
  booking: bookingShape,
  detail: historyDetailShape.isRequired
};

export default injectIntl(HistoryActionBookingItems);
